import { Component, computed, effect, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { FeedbackCampaignService } from './feedback-campaign.service';

const MAX_COMMENT_LENGTH = 500;
const SCORES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

@Component({
  selector: 'app-feedback-widget',
  imports: [MatButtonModule, MatFormFieldModule, MatInputModule, MatProgressSpinnerModule],
  templateUrl: './feedback-widget.component.html',
  styleUrl: './feedback-widget.component.scss',
})
export class FeedbackWidgetComponent {
  protected readonly feedbackCampaignService = inject(FeedbackCampaignService);

  protected readonly campaign = this.feedbackCampaignService.campaign;
  protected readonly isVisible = this.feedbackCampaignService.isVisible;
  protected readonly isSubmitting = this.feedbackCampaignService.isSubmitting;
  protected readonly scores = SCORES;
  protected readonly maxCommentLength = MAX_COMMENT_LENGTH;

  protected readonly selectedScore = signal<number | null>(null);
  protected readonly comment = signal('');
  protected readonly remainingCharacters = computed(
    () => MAX_COMMENT_LENGTH - this.comment().length
  );
  protected readonly canSubmit = computed(
    () => this.selectedScore() !== null && !this.isSubmitting()
  );

  constructor() {
    effect(() => {
      const campaignId = this.campaign()?.id;
      if (!campaignId) {
        return;
      }

      this.resetForm();
    });
  }

  protected selectScore(score: number): void {
    this.selectedScore.set(score);
  }

  protected updateComment(event: Event): void {
    const value = (event.target as HTMLTextAreaElement).value.slice(0, MAX_COMMENT_LENGTH);
    this.comment.set(value);
  }

  protected skip(): void {
    this.feedbackCampaignService.skipCurrentCampaign();
    this.resetForm();
  }

  protected dontShowAgain(): void {
    this.feedbackCampaignService.dontShowAgainCurrentCampaign();
    this.resetForm();
  }

  protected async submit(): Promise<void> {
    const score = this.selectedScore();
    if (score === null) {
      return;
    }

    const submitted = await this.feedbackCampaignService.submitCurrentCampaign(
      score,
      this.comment()
    );

    if (submitted) {
      this.resetForm();
    }
  }

  private resetForm(): void {
    this.selectedScore.set(null);
    this.comment.set('');
  }
}
